/* ───────────────────────────────────────────────────────────────────────────
   StudentPortal — the TUAP student's portal.

   A multi-tab workspace, shared by the student (read-only) and the NM Squad
   team (editable, role="team"): Profile, Credentials, NTT, Interests, Values,
   Academic, Activities. Edits persist to the tuapStudents doc.
   ─────────────────────────────────────────────────────────────────────────── */

import { useState, useEffect, useMemo, useRef } from "react";
import {
  INTEREST_SUBJECTS, ONB_DOCUMENTS,
  updateTuapStudent, getTuapFiles, uploadOnbFile,
  getLeadNtt, nttTestLink,
} from "./tuap";
import Lightbox from "./Lightbox";
import mascotImg from "./mascot.png";
import { EssaysTab } from "./EssayPanel";
import {
  COLLEGE_NAMES, collegeDomain, collegeInfo, COL_RISK, COL_ROUND,
  COL_SCHOLARSHIP, COL_DEADLINE,
} from "./colleges";

/* ── Palette: clean white + gold ── */
const GOLD = "#F2B100", GOLD_LO = "#8A6400";
const INK = "#1A1A1E", TXT = "#5C5C66", MUTE = "#9A9AA2";
const LINE = "#E6E6E9", BG = "#F6F6F7", CARD = "#FFFFFF", TINT = "#FFF7DD";

function Styles() {
  return (
    <style>{`
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&family=Space+Mono:wght@400;700&family=Newsreader:ital,opsz@1,6..72&display=swap');
.sp,.sp *{box-sizing:border-box;margin:0;}
.sp{font-family:'Archivo',-apple-system,sans-serif;color:${TXT};-webkit-font-smoothing:antialiased;}
.sp-disp{font-family:'Archivo',sans-serif;font-weight:900;letter-spacing:-.02em;}
.sp-mono{font-family:'Space Mono',monospace;text-transform:uppercase;letter-spacing:.06em;}
.sp-ital{font-family:'Newsreader',serif;font-style:italic;}
.sp-tab{font-family:'Space Mono',monospace;font-size:12px;font-weight:700;letter-spacing:.05em;
  text-transform:uppercase;color:${MUTE};padding:8px 4px;border:none;background:transparent;
  cursor:pointer;white-space:nowrap;border-bottom:2.5px solid transparent;transition:.13s;}
.sp-tab:hover{color:${INK};}
.sp-tab.on{color:${INK};border-bottom-color:${GOLD};}
.sp-in{width:100%;font-family:'Archivo',sans-serif;font-size:14px;font-weight:500;color:${INK};
  padding:10px 12px;border:1.5px solid ${LINE};border-radius:6px;background:#fff;outline:none;
  transition:border-color .13s;}
.sp-in:focus{border-color:${GOLD};box-shadow:0 0 0 3px rgba(200,146,12,.16);}
.sp-in:disabled{background:#F1F1F2;color:${TXT};}
.sp-btn{font-family:'Space Mono',monospace;font-size:11.5px;font-weight:700;letter-spacing:.05em;
  text-transform:uppercase;cursor:pointer;border:1.5px solid ${INK};border-radius:6px;
  padding:9px 15px;background:${INK};color:${CARD};}
.sp-btn.ghost{background:transparent;border:1.5px solid ${LINE};color:${INK};}
.sp-btn.gold{background:${GOLD};border-color:${GOLD};color:#fff;}
.sp-card{background:${CARD};border:1.5px solid ${LINE};border-radius:8px;}
.sp-blink{animation:spBlink 1s steps(1,end) infinite;}
@keyframes spBlink{50%{opacity:.25;}}
.sp-rise{animation:spRise .3s ease both;}
@keyframes spRise{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
`}</style>
  );
}

/* ── Account directory for the Credentials tab ── */
const ACCOUNTS = [
  ["CommonApp", "commonapp.org"], ["Apply UC", "universityofcalifornia.edu"],
  ["UCAS", "ucas.com"], ["OUAC", "ouac.on.ca"], ["UBC", "ubc.ca"],
  ["MIT", "mit.edu"], ["Apply Texas", "goapplytexas.org"],
  ["Coalition", "coalitionforcollegeaccess.org"], ["NovoResume", "novoresume.com"],
  ["SAT", "collegeboard.org"], ["ACT", "act.org"], ["Gmail", "gmail.com"],
  ["TOEFL", "ets.org"], ["IELTS", "ielts.org"], ["DET", "duolingo.com"],
];
const CURRICULA = ["IB-MYP", "IBDP", "IGCSE", "A-Levels", "AP", "CBSE", "ICSE", "State Board", "Other"];
const MAJOR_CLASS = ["STEM", "Business & Economics", "Humanities", "Social Sciences",
  "Arts & Design", "Health & Medicine", "Law", "Undecided"];
const ACT_TYPES = ["Research", "Academic", "Art", "Athletics: Club", "Athletics: JV/Varsity",
  "Career Oriented", "Community Service (Volunteer)", "Computer/ Technology", "Cultural",
  "Dance", "Debate/ Speech", "Environmental", "Family Responsibilities", "Foreign Exchange",
  "Internship", "Journalism", "Junior R.O.T.C.", "LGBT", "Music: Instrumental", "Music: Vocal",
  "Religious", "Robotics", "School Spirit", "Science/ Math", "Social Govt./ Politics",
  "Theatre/ Drama", "Work (Paid)", "Other Club"];

function logoFor(domain) { return `https://logo.clearbit.com/${domain}`; }
function faviconFor(domain) { return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`; }

function Logo({ domain, size = 30 }) {
  const [src, setSrc] = useState(logoFor(domain));
  return (
    <img src={src} alt="" width={size} height={size}
      onError={() => setSrc(faviconFor(domain))}
      style={{ borderRadius: 7, objectFit: "contain", background: "#fff", flexShrink: 0 }} />
  );
}

function fileKind(f) { return f && /^image\//.test(f.type || "") ? "img" : "doc"; }

/* ════════════════ PROFILE ════════════════ */
function ProfileTab({ a, photoUrl, files = {}, onView, team, save }) {
  // Inline-editable row. Team mode: input that saves on blur with the parent's
  // debounced save(); student/read-only mode: plain text (hidden if empty).
  const Row = ({ label, k, value, placeholder }) => {
    if (!team && !value) return null;
    return (
      <div style={{ padding: "10px 0", borderBottom: `1px solid ${LINE}`, display: "flex",
        justifyContent: "space-between", gap: 14, alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: MUTE, minWidth: 110 }}>{label}</span>
        {team ? (
          <input defaultValue={value || ""} placeholder={placeholder || "—"}
            onBlur={e => {
              const v = e.target.value;
              if (v !== (value || "")) save({ onbAnswers: { ...a, [k]: v } });
            }}
            style={{ flex: 1, textAlign: "right", border: "none", outline: "none",
              background: "transparent", fontSize: 13.5, fontWeight: 700, color: INK,
              fontFamily: "inherit" }} />
        ) : (
          <span style={{ fontSize: 13.5, fontWeight: 700, color: INK, textAlign: "right" }}>
            {value}
          </span>
        )}
      </div>
    );
  };
  const name = [a.firstName, a.middleName, a.lastName].filter(Boolean).join(" ");
  return (
    <div className="sp-rise" style={{ display: "grid", gap: 16,
      gridTemplateColumns: "minmax(0,1fr)" }}>
      <div className="sp-card" style={{ padding: 22, display: "flex", gap: 18, alignItems: "center",
        flexWrap: "wrap" }}>
        {photoUrl
          ? <img src={photoUrl} alt="" style={{ width: 96, height: 96, borderRadius: 18,
              objectFit: "cover", border: `3px solid ${GOLD}` }} />
          : <div style={{ width: 96, height: 96, borderRadius: 18, background: TINT,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 34, fontWeight: 800, color: GOLD_LO }}>{(name || "S")[0]}</div>}
        <div>
          <div className="sp-disp" style={{ fontSize: 26, fontWeight: 600, color: INK }}>
            {name || "Student"}</div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: TXT, marginTop: 2 }}>
            {a.currentGrade || ""}{a.intendedMajor ? ` · ${a.intendedMajor}` : ""}</div>
        </div>
      </div>
      <div className="sp-card" style={{ padding: "6px 20px" }}>
        <Row label="Email"          k="email"        value={a.email} />
        <Row label="Phone"          k="studentPhone" value={a.studentPhone} />
        <Row label="Date of birth"  k="dob"          value={a.dob} />
        <Row label="Gender"         k="gender"       value={a.gender} />
        <Row label="Address"        k="address"      value={a.address} />
        <Row label="Citizenship"    k="citizenship"  value={a.citizenship} />
        <Row label="Birth city"     k="birthCity"    value={a.birthCity} />
        <Row label="Birth country"  k="birthCountry" value={a.birthCountry} />
        <Row label="Religion"       k="religion"     value={a.religion} />
        <Row label="Food pref"      k="foodPref"     value={a.foodPref} />
        <Row label="Intended major" k="intendedMajor" value={a.intendedMajor} />
      </div>
      <div className="sp-card" style={{ padding: "6px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO, letterSpacing: ".06em",
          padding: "12px 0 2px" }}>FATHER</div>
        <Row label="First name"  k="fatherFirst"      value={a.fatherFirst} />
        <Row label="Last name"   k="fatherLast"       value={a.fatherLast} />
        <Row label="Email"       k="fatherEmail"      value={a.fatherEmail} />
        <Row label="Phone"       k="fatherPhone"      value={a.fatherPhone} />
        <Row label="Occupation"  k="fatherOccupation" value={a.fatherOccupation} />
        <Row label="Education"   k="fatherEducation"  value={a.fatherEducation} />
        <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO, letterSpacing: ".06em",
          padding: "14px 0 2px" }}>MOTHER</div>
        <Row label="First name"  k="motherFirst"      value={a.motherFirst} />
        <Row label="Last name"   k="motherLast"       value={a.motherLast} />
        <Row label="Email"       k="motherEmail"      value={a.motherEmail} />
        <Row label="Phone"       k="motherPhone"      value={a.motherPhone} />
        <Row label="Occupation"  k="motherOccupation" value={a.motherOccupation} />
        <Row label="Education"   k="motherEducation"  value={a.motherEducation} />
      </div>
      {(ONB_DOCUMENTS.some(d => files[d.key])
        || Object.values(a.docsDeferred || {}).some(Boolean)) && (
        <div className="sp-card" style={{ padding: "16px 20px" }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO, letterSpacing: ".06em",
            marginBottom: 10 }}>DOCUMENTS</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ONB_DOCUMENTS.filter(d => files[d.key]).map(d => (
              <button key={d.key} onClick={() => onView({ ...files[d.key], name: d.label })}
                style={{ fontSize: 12.5, fontWeight: 700, color: INK, background: TINT,
                  border: `1.5px solid ${LINE}`, borderRadius: 9, padding: "8px 12px",
                  cursor: "pointer" }}>{d.label}</button>
            ))}
            {ONB_DOCUMENTS.filter(d => (a.docsDeferred || {})[d.key] && !files[d.key])
              .map(d => (
                <span key={d.key} title="Student said they'll submit this later"
                  style={{ fontSize: 12.5, fontWeight: 700, color: MUTE, background: "#F4F4F6",
                    border: `1.5px dashed ${LINE}`, borderRadius: 9, padding: "8px 12px" }}>
                  {d.label} · later
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════ CREDENTIALS ════════════════ */
function CredRow({ cred, team, onChange, onDelete }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState("");
  const acct = ACCOUNTS.find(x => x[0] === cred.account);
  const domain = acct ? acct[1] : (cred.customUrl || "").replace(/^https?:\/\//, "").split("/")[0];
  const copy = (text, what) => {
    navigator.clipboard.writeText(text || "");
    setCopied(what); setTimeout(() => setCopied(""), 1200);
  };
  return (
    <div className="sp-card" style={{ padding: 14, display: "flex", gap: 13, alignItems: "center",
      flexWrap: "wrap" }}>
      <Logo domain={domain || "globe"} size={34} />
      <div style={{ minWidth: 110, flex: "0 0 auto" }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
          {cred.account === "Other" ? (cred.customName || "Other") : cred.account}
        </div>
        {(cred.customUrl || domain) && (
          <a href={(cred.customUrl || `https://${domain}`)} target="_blank" rel="noreferrer"
            style={{ fontSize: 11, fontWeight: 700, color: GOLD_LO, textDecoration: "none" }}>
            Open site
          </a>
        )}
      </div>
      <div style={{ flex: "1 1 150px", display: "flex", gap: 6, alignItems: "center" }}>
        <input className="sp-in" disabled={!team} placeholder="Username"
          value={cred.username || ""} onChange={e => onChange({ ...cred, username: e.target.value })} />
        <button className="sp-btn ghost" onClick={() => copy(cred.username, "u")}
          style={{ padding: "8px 10px" }}>{copied === "u" ? "✓" : "Copy"}</button>
      </div>
      <div style={{ flex: "1 1 150px", display: "flex", gap: 6, alignItems: "center" }}>
        <input className="sp-in" disabled={!team} type={show ? "text" : "password"}
          placeholder="Password" value={cred.password || ""}
          onChange={e => onChange({ ...cred, password: e.target.value })} />
        <button className="sp-btn ghost" onClick={() => setShow(!show)}
          style={{ padding: "8px 10px" }}>{show ? "Hide" : "View"}</button>
        <button className="sp-btn ghost" onClick={() => copy(cred.password, "p")}
          style={{ padding: "8px 10px" }}>{copied === "p" ? "✓" : "Copy"}</button>
      </div>
      {team && (
        <button onClick={onDelete} className="sp-btn ghost"
          style={{ padding: "8px 10px", color: "#d33" }}>Remove</button>
      )}
    </div>
  );
}

function CredentialsTab({ creds, team, save }) {
  const [picker, setPicker] = useState(false);
  function add(account) {
    save([...creds, { id: Date.now().toString(36), account, customName: "", customUrl: "",
      username: "", password: "" }]);
    setPicker(false);
  }
  return (
    <div className="sp-rise">
      {creds.length === 0 && (
        <div className="sp-card" style={{ padding: "34px 22px", textAlign: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>No accounts yet</div>
          <div style={{ fontSize: 13, color: MUTE, marginTop: 4 }}>
            {team ? "Add the accounts you create for this student."
              : "Your NM Squad team will add your application accounts here."}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {creds.map((c, i) => (
          <div key={c.id || i}>
            {c.account === "Other" && team && (
              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input className="sp-in" placeholder="Account name"
                  value={c.customName || ""}
                  onChange={e => save(creds.map((x, j) => j === i ? { ...x, customName: e.target.value } : x))} />
                <input className="sp-in" placeholder="https://site.com"
                  value={c.customUrl || ""}
                  onChange={e => save(creds.map((x, j) => j === i ? { ...x, customUrl: e.target.value } : x))} />
              </div>
            )}
            <CredRow cred={c} team={team}
              onChange={nc => save(creds.map((x, j) => j === i ? nc : x))}
              onDelete={() => save(creds.filter((_, j) => j !== i))} />
          </div>
        ))}
      </div>
      {team && (
        <div style={{ marginTop: 14 }}>
          {picker ? (
            <div className="sp-card" style={{ padding: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[...ACCOUNTS.map(a => a[0]), "Other"].map(name => (
                <button key={name} className="sp-btn ghost" onClick={() => add(name)}>{name}</button>
              ))}
            </div>
          ) : (
            <button className="sp-btn" onClick={() => setPicker(true)}>+ Add account</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════ NTT ════════════════ */
const NTT_WS_NAMES = { sd:"Spot the Match", type:"Fast Fingers", word:"First Word",
  idea:"Idea Storm", pairs:"Word Pairs", design:"Pattern Recall", fold:"Fold & Find" };
function NttTab({ student, files, team, save, onUpload }) {
  const f = files.nttScore;
  // The online NTT lives on the originating FM lead (tuapStudent.fmLeadId).
  const [ntt, setNtt] = useState(null);
  const [nttLoad, setNttLoad] = useState(!!student.fmLeadId);
  useEffect(() => {
    let live = true;
    if (student.fmLeadId) {
      getLeadNtt(student.fmLeadId)
        .then(d => { if (live) { setNtt(d); setNttLoad(false); } })
        .catch(() => { if (live) setNttLoad(false); });
    }
    return () => { live = false; };
  }, [student.fmLeadId]);
  const status = ntt && ntt.nttStatus;
  const link = ntt && ntt.nttToken ? nttTestLink(ntt) : null;
  return (
    <div className="sp-rise" style={{ display: "grid", gap: 16 }}>
      {(nttLoad || status) && (
        <div className="sp-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 4 }}>
            Online Natural Talent Test</div>
          {nttLoad ? (
            <div style={{ fontSize: 13, color: MUTE }}>Checking your test status…</div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 12 }}>
                {status === "completed"
                  ? "Completed — thank you. Your counsellor will walk you through what your natural strengths mean for your studies and career."
                  : status === "in_progress"
                  ? "You've started the test. Pick up right where you left off."
                  : "Your test is ready. Find a quiet 30–40 minutes and complete it in one sitting."}
              </div>
              {status !== "completed" && link && !team && (
                <a className="sp-btn" href={link} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-block", textDecoration: "none" }}>
                  {status === "in_progress" ? "Resume the test" : "Take the test now"}</a>
              )}
              {status === "completed" && ntt.nttResults && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.keys(ntt.nttResults).map(k => (
                    <span key={k} style={{ padding: "4px 10px", background: "#f0fdf4",
                      border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 11.5,
                      fontWeight: 700, color: "#15803d" }}>{NTT_WS_NAMES[k] || k}</span>
                  ))}
                </div>
              )}
              {team && link && (
                <a className="sp-btn ghost" href={link} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-block", textDecoration: "none", marginTop: 10 }}>
                  Open the student's test link</a>
              )}
            </>
          )}
        </div>
      )}
      <div className="sp-card" style={{ padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 4 }}>
          Natural Talents Test scorecard</div>
        <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 12 }}>
          {team ? "Upload the student's NTT scorecard." : "Your NTT scorecard."}</div>
        {f ? (
          fileKind(f) === "img"
            ? <img src={f.data} alt="NTT" style={{ width: "100%", borderRadius: 12,
                border: `1px solid ${LINE}` }} />
            : <iframe title="NTT scorecard" src={f.data} style={{ width: "100%", height: 520,
                border: `1px solid ${LINE}`, borderRadius: 12, background: "#fff" }} />
        ) : (
          <div style={{ fontSize: 13, color: MUTE }}>No scorecard uploaded yet.</div>
        )}
        {team && (
          <label className="sp-btn ghost" style={{ display: "inline-block", marginTop: 12 }}>
            {f ? "Replace scorecard" : "Upload scorecard"}
            <input type="file" accept="image/*,application/pdf" style={{ display: "none" }}
              onChange={e => e.target.files[0] && onUpload("nttScore", e.target.files[0])} />
          </label>
        )}
      </div>
      <div className="sp-card" style={{ padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 8 }}>
          Counsellor's notes</div>
        <textarea className="sp-in" disabled={!team} rows={5}
          placeholder={team ? "Neeraj sir's notes on the scorecard…" : "Notes from your counsellor will appear here."}
          defaultValue={student.nttNotes || ""}
          onBlur={e => team && save({ nttNotes: e.target.value })}
          style={{ resize: "vertical", lineHeight: 1.5 }} />
      </div>
    </div>
  );
}

/* ════════════════ INTERESTS / VALUES (response lists) ════════════════ */
const COLORS = ["", "#FEE2E2", "#FEF3C7", "#DCFCE7", "#DBEAFE", "#EDE9FE"];
// `order` is the rating options best-first; drives sorting and colour.
function RatingList({ blurb, items, order, team, meta, save }) {
  const [filter, setFilter] = useState("All");
  const rank = r => { const i = order.indexOf(r); return i < 0 ? 99 : i; };
  const sorted = useMemo(() => [...items].sort(
    (a, b) => rank(a.rating) - rank(b.rating)), [items]); // eslint-disable-line
  function setMeta(key, patch) {
    save({ ...meta, [key]: { ...(meta[key] || {}), ...patch } });
  }
  const shown = sorted.filter(it => (team || !(meta[it.key] || {}).hidden)
    && (filter === "All" || it.rating === filter));
  // When only one rating bucket exists (top-responses view), skip the filter row.
  const showFilters = order.length > 1;
  return (
    <div className="sp-rise">
      <div style={{ fontSize: 13, color: MUTE, fontWeight: 600, marginBottom: 12 }}>
        {blurb}{!showFilters && ` ${sorted.length} total.`}
      </div>
      {showFilters && (
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
          {["All", ...order].map(c => {
            const n = c === "All" ? sorted.length : sorted.filter(x => x.rating === c).length;
            const on = filter === c;
            return (
              <button key={c} className="sp-mono" onClick={() => setFilter(c)}
                style={{ fontSize: 10.5, fontWeight: 700, padding: "6px 11px", borderRadius: 6,
                  cursor: "pointer", border: `1.5px solid ${on ? INK : LINE}`,
                  background: on ? INK : CARD, color: on ? CARD : TXT }}>
                {c} · {n}
              </button>
            );
          })}
        </div>
      )}
      {shown.length === 0 && (
        <div className="sp-card" style={{ padding: "30px 20px", textAlign: "center",
          fontSize: 13.5, fontWeight: 700, color: MUTE }}>No responses in this filter.</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {shown.map(it => {
          const m = meta[it.key] || {};
          const ri = order.indexOf(it.rating);
          const rc = ri === 0 ? "#15803d" : ri === order.length - 1 ? MUTE : GOLD_LO;
          return (
            <div key={it.key} className="sp-card" style={{ padding: "11px 14px",
              background: m.color || CARD, display: "flex", alignItems: "center",
              gap: 12, flexWrap: "wrap", opacity: m.hidden ? .5 : 1 }}>
              <span style={{ flex: "1 1 auto", fontSize: 13.5, fontWeight: 700, color: INK }}>
                {it.label}</span>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: rc }}>
                {(it.rating || "").replace(" Interested", "") || "—"}</span>
              {team && (
                <>
                  <div style={{ display: "flex", gap: 4 }}>
                    {COLORS.map(c => (
                      <button key={c || "none"} onClick={() => setMeta(it.key, { color: c })}
                        title="Colour" style={{ width: 18, height: 18, borderRadius: 5,
                          cursor: "pointer", background: c || "#fff",
                          border: `1.5px solid ${m.color === c ? INK : LINE}` }} />
                    ))}
                  </div>
                  <button className="sp-btn ghost" style={{ padding: "6px 10px" }}
                    onClick={() => setMeta(it.key, { hidden: !m.hidden })}>
                    {m.hidden ? "Restore" : "Remove"}</button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════ ACADEMIC ════════════════ */
function AcademicTab({ a, student, files, team, save, onUpload, onView }) {
  const ac = student.academic || {};
  const school = ac.school || {};
  const grades = ["9", "10", "11", "12"];
  function setSchool(g, patch) {
    save({ academic: { ...ac, school: { ...school, [g]: { ...(school[g] || {}), ...patch } } } });
  }
  return (
    <div className="sp-rise" style={{ display: "grid", gap: 16 }}>
      {/* Major */}
      <div className="sp-card" style={{ padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO, letterSpacing: ".06em",
          marginBottom: 12 }}>MAJOR</div>
        {[["majorActual", "Projected Major (Actual)", "text"],
          ["majorClass", "Projected Major (Classification)", "select"],
          ["majorComments", "Comments", "text"]].map(([k, label, type]) => (
          <div key={k} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: TXT, marginBottom: 5 }}>{label}</div>
            {type === "select" ? (
              <select className="sp-in" disabled={!team} value={ac[k] || ""}
                onChange={e => save({ academic: { ...ac, [k]: e.target.value } })}>
                <option value="">Select…</option>
                {MAJOR_CLASS.map(o => <option key={o}>{o}</option>)}
              </select>
            ) : (
              <input className="sp-in" disabled={!team} defaultValue={ac[k] || ""}
                onBlur={e => team && save({ academic: { ...ac, [k]: e.target.value } })} />
            )}
          </div>
        ))}
      </div>

      {/* School grid */}
      <div className="sp-card" style={{ padding: 20, overflowX: "auto" }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO, letterSpacing: ".06em",
          marginBottom: 12 }}>SCHOOL</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
          <thead><tr>
            {["Grade", "School", "Curriculum", "Subjects", "Scorecard"].map(h => (
              <th key={h} style={{ textAlign: "left", padding: "6px 8px", fontSize: 11,
                fontWeight: 900, color: MUTE }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {grades.map(g => {
              const s = school[g] || {};
              const sf = files[`acad${g}`];
              const onbName = a[`school${g}`] || "";
              return (
                <tr key={g} style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={{ padding: "8px", fontWeight: 800, color: INK }}>Grade {g}</td>
                  <td style={{ padding: "8px" }}>
                    <input className="sp-in" disabled={!team} placeholder={onbName || "School"}
                      defaultValue={s.name || onbName}
                      onBlur={e => team && setSchool(g, { name: e.target.value })} />
                  </td>
                  <td style={{ padding: "8px" }}>
                    <select className="sp-in" disabled={!team} value={s.curriculum || ""}
                      onChange={e => setSchool(g, { curriculum: e.target.value })}>
                      <option value="">—</option>
                      {CURRICULA.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <input className="sp-in" disabled={!team} placeholder="Subjects"
                      defaultValue={s.subjects || ""}
                      onBlur={e => team && setSchool(g, { subjects: e.target.value })} />
                  </td>
                  <td style={{ padding: "8px", whiteSpace: "nowrap" }}>
                    {sf ? (
                      <button onClick={() => onView({ ...sf, name: `Grade ${g} scorecard` })}
                        style={{ fontSize: 12, fontWeight: 800, color: GOLD_LO, background: "none",
                          border: "none", cursor: "pointer", padding: 0 }}>View</button>
                    ) : team ? (
                      <label style={{ fontSize: 12, fontWeight: 800, color: GOLD_LO, cursor: "pointer" }}>
                        Upload
                        <input type="file" accept="image/*,application/pdf" style={{ display: "none" }}
                          onChange={e => e.target.files[0] && onUpload(`acad${g}`, e.target.files[0])} />
                      </label>
                    ) : (
                      <span className="sp-blink" style={{ fontSize: 11, fontWeight: 800,
                        color: "#dc2626" }}>Not uploaded</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════ ACTIVITIES ════════════════ */
const BLANK_ACT = { type: "", position: "", organization: "", description: "",
  grades: "", when: "", hoursWeek: "", weeksYear: "", cont: "" };
function ActivitiesTab({ acts, team, save }) {
  function setRow(i, patch) { save(acts.map((r, j) => j === i ? { ...r, ...patch } : r)); }
  return (
    <div className="sp-rise">
      <div style={{ fontSize: 13, color: MUTE, fontWeight: 600, marginBottom: 12 }}>
        Activities and honors, ranked. Descriptions should land under 150 characters
        (CommonApp-style) — you can type beyond while brainstorming; it just flags red.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {acts.map((r, i) => {
          const cc = (r.description || "").length;
          const over = cc > 150;
          return (
            <div key={i} className="sp-card" style={{ padding: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 9 }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: GOLD,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 900, color: INK }}>{i + 1}</span>
                <select className="sp-in" disabled={!team} value={r.type || ""}
                  onChange={e => setRow(i, { type: e.target.value })} style={{ maxWidth: 220 }}>
                  <option value="">Activity type…</option>
                  {ACT_TYPES.map(o => <option key={o}>{o}</option>)}
                </select>
                {team && (
                  <button className="sp-btn ghost" style={{ marginLeft: "auto", color: "#d33" }}
                    onClick={() => save(acts.filter((_, j) => j !== i))}>Remove</button>
                )}
              </div>
              <div style={{ display: "grid", gap: 8,
                gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
                <input className="sp-in" disabled={!team} placeholder="Position / role"
                  defaultValue={r.position || ""} onBlur={e => team && setRow(i, { position: e.target.value })} />
                <input className="sp-in" disabled={!team} placeholder="Organization"
                  defaultValue={r.organization || ""} onBlur={e => team && setRow(i, { organization: e.target.value })} />
                <input className="sp-in" disabled={!team} placeholder="Grades (e.g. 11, 12)"
                  defaultValue={r.grades || ""} onBlur={e => team && setRow(i, { grades: e.target.value })} />
                <input className="sp-in" disabled={!team} placeholder="When (e.g. All Year)"
                  defaultValue={r.when || ""} onBlur={e => team && setRow(i, { when: e.target.value })} />
                <input className="sp-in" disabled={!team} placeholder="Hours/week"
                  defaultValue={r.hoursWeek || ""} onBlur={e => team && setRow(i, { hoursWeek: e.target.value })} />
                <input className="sp-in" disabled={!team} placeholder="Weeks/year"
                  defaultValue={r.weeksYear || ""} onBlur={e => team && setRow(i, { weeksYear: e.target.value })} />
                <select className="sp-in" disabled={!team} value={r.cont || ""}
                  onChange={e => setRow(i, { cont: e.target.value })}>
                  <option value="">Continue?</option>
                  <option>Yes</option><option>No</option>
                </select>
              </div>
              <div style={{ marginTop: 8 }}>
                <textarea className="sp-in" disabled={!team} rows={3} placeholder="Description"
                  value={r.description || ""}
                  onChange={e => team && setRow(i, { description: e.target.value })}
                  style={{ resize: "vertical", lineHeight: 1.5,
                    borderColor: over ? "#dc2626" : LINE }} />
                <div style={{ fontSize: 11.5, fontWeight: 800, marginTop: 4,
                  color: over ? "#dc2626" : MUTE }}>
                  {cc} / 150 characters{over ? " — over the limit, trim before finalising" : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {team && (
        <button className="sp-btn" style={{ marginTop: 14 }}
          onClick={() => save([...acts, { ...BLANK_ACT }])}>+ Add activity</button>
      )}
    </div>
  );
}

/* ════════════════ COLLEGE LIST ════════════════ */
function CollegeField({ label, value, opts, team, onChange }) {
  return (
    <div>
      <span className="sp-mono" style={{ fontSize: 10, color: MUTE, display: "block",
        marginBottom: 4 }}>{label}</span>
      {opts ? (
        <select className="sp-in" disabled={!team} value={value || ""}
          onChange={e => onChange(e.target.value)}>
          <option value="">—</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input className="sp-in" disabled={!team} value={value || ""} placeholder="—"
          onChange={e => onChange(e.target.value)} />
      )}
    </div>
  );
}
function CollegeStat({ label, value }) {
  return (
    <div>
      <span className="sp-mono" style={{ fontSize: 10, color: MUTE, display: "block",
        marginBottom: 4 }}>{label}</span>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: INK, padding: "9px 0" }}>
        {value || "—"}</div>
    </div>
  );
}
function CollegeRow({ c, team, onChange, onRemove }) {
  const risk = COL_RISK.find(r => r.label === c.risk);
  const set = (k, v) => onChange({ ...c, [k]: v });
  return (
    <div className="sp-card" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <Logo domain={collegeDomain(c.college) || "globe"} size={44} />
        <div style={{ flex: 1, fontSize: 15.5, fontWeight: 800, color: INK }}>{c.college}</div>
        {risk && (
          <span style={{ fontSize: 11, fontWeight: 800, color: risk.fg, background: risk.bg,
            padding: "5px 11px", borderRadius: 999, whiteSpace: "nowrap" }}>{risk.label}</span>
        )}
        {team && (
          <button className="sp-btn ghost" style={{ padding: "6px 11px" }}
            onClick={onRemove}>Remove</button>
        )}
      </div>
      <div style={{ display: "grid", gap: "12px 14px",
        gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }}>
        <CollegeField label="Risk" value={c.risk} team={team}
          opts={COL_RISK.map(r => r.label)} onChange={v => set("risk", v)} />
        <CollegeStat label="Country" value={c.country} />
        <CollegeField label="Major" value={c.major} team={team}
          onChange={v => set("major", v)} />
        <CollegeField label="Round" value={c.round} team={team}
          opts={COL_ROUND} onChange={v => set("round", v)} />
        <CollegeStat label="Portal" value={c.portal} />
        <CollegeField label="Scholarship" value={c.scholarship} team={team}
          opts={COL_SCHOLARSHIP} onChange={v => set("scholarship", v)} />
        <CollegeField label="NM Deadline" value={c.deadline} team={team}
          opts={COL_DEADLINE} onChange={v => set("deadline", v)} />
        <CollegeField label="Flexibility" value={c.flexibility} team={team}
          opts={["Flexible", "Semi-Flexible", "Rigid"]} onChange={v => set("flexibility", v)} />
      </div>
      <div style={{ marginTop: 12 }}>
        <span className="sp-mono" style={{ fontSize: 10, color: MUTE, display: "block",
          marginBottom: 4 }}>Comments</span>
        <textarea className="sp-in" disabled={!team} rows={2} value={c.comments || ""}
          onChange={e => set("comments", e.target.value)} style={{ resize: "vertical" }} />
      </div>
    </div>
  );
}
function CollegeListTab({ colleges, team, save }) {
  const [pick, setPick] = useState("");
  function add(name) {
    if (!name) return;
    const info = collegeInfo(name);
    save([...colleges, { id: Date.now().toString(36), college: name, risk: "",
      country: info.country, major: "", round: "", portal: info.portal,
      scholarship: "", deadline: "", comments: "" }]);
    setPick("");
  }
  return (
    <div className="sp-rise">
      <div style={{ fontSize: 13, color: MUTE, fontWeight: 600, marginBottom: 14 }}>
        The college list — every college with its application details.
      </div>
      {colleges.length === 0 && (
        <div className="sp-card" style={{ padding: "32px 20px", textAlign: "center",
          fontSize: 13.5, fontWeight: 700, color: MUTE }}>
          {team ? "No colleges added yet — add the first below." : "No colleges added yet."}
        </div>
      )}
      <div style={{ display: "grid", gap: 12 }}>
        {colleges.map((c, i) => (
          <CollegeRow key={c.id || i} c={c} team={team}
            onChange={nc => save(colleges.map((x, j) => j === i ? nc : x))}
            onRemove={() => save(colleges.filter((_, j) => j !== i))} />
        ))}
      </div>
      {team && (
        <div style={{ marginTop: 14 }}>
          <select className="sp-in" value={pick} onChange={e => add(e.target.value)}
            style={{ maxWidth: 360 }}>
            <option value="">+ Add a college…</option>
            {COLLEGE_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

/* ════════════════ RECOMMENDATIONS ════════════════ */
const RECO_ROLES = ["Counselor", "Teacher 1", "Teacher 2", "Teacher 3", "Alumni", "External"];
function RecoFile({ label, fileKey, files, team, uploading, onUpload, onView }) {
  const f = files[fileKey];
  return (
    <div>
      <span className="sp-mono" style={{ fontSize: 10, color: MUTE, display: "block",
        marginBottom: 5 }}>{label}</span>
      {f ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="sp-btn ghost" style={{ padding: "7px 12px" }}
            onClick={() => onView({ ...f, name: label })}>View</button>
          {team && (
            <label className="sp-btn ghost" style={{ padding: "7px 12px", cursor: "pointer" }}>
              Replace
              <input type="file" accept="image/*,application/pdf" style={{ display: "none" }}
                onChange={e => e.target.files[0] && onUpload(fileKey, e.target.files[0])} />
            </label>
          )}
        </div>
      ) : team ? (
        <label className="sp-btn ghost" style={{ padding: "8px 13px", cursor: "pointer",
          display: "inline-block" }}>
          {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*,application/pdf" style={{ display: "none" }}
            onChange={e => e.target.files[0] && onUpload(fileKey, e.target.files[0])} />
        </label>
      ) : (
        <span style={{ fontSize: 12.5, color: MUTE, fontWeight: 600 }}>Not uploaded yet</span>
      )}
    </div>
  );
}
function RecommendationsTab({ recos, team, save, files, uploading, onUpload, onView }) {
  return (
    <div className="sp-rise">
      <div style={{ fontSize: 13, color: MUTE, fontWeight: 600, marginBottom: 14 }}>
        Recommenders, their contact details, the draft, and the final sealed &amp; stamped letter.
      </div>
      {recos.length === 0 && (
        <div className="sp-card" style={{ padding: "32px 20px", textAlign: "center",
          fontSize: 13.5, fontWeight: 700, color: MUTE }}>
          {team ? "No recommenders added yet." : "No recommenders added yet."}
        </div>
      )}
      <div style={{ display: "grid", gap: 12 }}>
        {recos.map((r, i) => {
          const set = (k, v) => save(recos.map((x, j) => j === i ? { ...x, [k]: v } : x));
          return (
            <div key={r.id || i} className="sp-card" style={{ padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 15.5, fontWeight: 800, color: INK, flex: 1 }}>
                  {r.name || "New recommender"}</span>
                {r.role && (
                  <span className="sp-mono" style={{ fontSize: 10, color: GOLD_LO,
                    border: `1px solid ${LINE}`, borderRadius: 5, padding: "3px 8px" }}>{r.role}</span>
                )}
                {team && (
                  <button className="sp-btn ghost" style={{ padding: "6px 11px" }}
                    onClick={() => save(recos.filter((_, j) => j !== i))}>Remove</button>
                )}
              </div>
              <div style={{ display: "grid", gap: "12px 14px",
                gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))" }}>
                <CollegeField label="Role" value={r.role} team={team}
                  opts={RECO_ROLES} onChange={v => set("role", v)} />
                <CollegeField label="Name" value={r.name} team={team}
                  onChange={v => set("name", v)} />
                <CollegeField label="Designation" value={r.designation} team={team}
                  onChange={v => set("designation", v)} />
                <CollegeField label="Phone" value={r.phone} team={team}
                  onChange={v => set("phone", v)} />
                <CollegeField label="Email" value={r.email} team={team}
                  onChange={v => set("email", v)} />
                <RecoFile label="Final PDF — sealed & stamped" fileKey={"recoFinal_" + (r.id || i)}
                  files={files} team={team} uploading={uploading["recoFinal_" + (r.id || i)]}
                  onUpload={onUpload} onView={onView} />
              </div>
              <div style={{ marginTop: 12 }}>
                <span className="sp-mono" style={{ fontSize: 10, color: MUTE, display: "block",
                  marginBottom: 4 }}>Draft letter</span>
                <textarea className="sp-in" disabled={!team} rows={6} value={r.draft || ""}
                  placeholder={team ? "Type or paste the recommendation letter draft…" : "No draft yet."}
                  onChange={e => set("draft", e.target.value)}
                  style={{ resize: "vertical", lineHeight: 1.6 }} />
                <div style={{ fontSize: 11.5, fontWeight: 800, color: MUTE, marginTop: 4 }}>
                  {(r.draft || "").trim() ? (r.draft.trim().split(/\s+/).length) : 0} words · {(r.draft || "").length} characters
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <span className="sp-mono" style={{ fontSize: 10, color: MUTE, display: "block",
                  marginBottom: 4 }}>Comments</span>
                <textarea className="sp-in" disabled={!team} rows={2} value={r.comments || ""}
                  onChange={e => set("comments", e.target.value)} style={{ resize: "vertical" }} />
              </div>
            </div>
          );
        })}
      </div>
      {team && (
        <button className="sp-btn" style={{ marginTop: 14 }}
          onClick={() => save([...recos, { id: Date.now().toString(36), role: "", name: "",
            designation: "", phone: "", email: "", comments: "" }])}>+ Add recommender</button>
      )}
    </div>
  );
}

/* ════════════════ PLAN ════════════════ */
const PLAN_MONTHS = [
  { m: "Aug", y: "'25" }, { m: "Sep", y: "'25" }, { m: "Oct", y: "'25" },
  { m: "Nov", y: "'25" }, { m: "Dec", y: "'25" }, { m: "Jan", y: "'26" },
  { m: "Feb", y: "'26" }, { m: "Mar", y: "'26" }, { m: "Apr", y: "'26" },
  { m: "May", y: "'26" }, { m: "Jun", y: "'26" }, { m: "Jul", y: "'26" },
  { m: "Aug", y: "'26" }, { m: "Sep", y: "'26" }, { m: "Oct", y: "'26" },
  { m: "Nov", y: "'26" }, { m: "Dec", y: "'26" },
];
const PLAN_COLORS = ["#22C55E", "#F59E0B", "#3B82F6", "#8B5CF6", "#EF4444"];
const planCell = v => (typeof v === "string" ? { t: v, c: 0 } : (v || { t: "", c: 0 }));
const esc = s => String(s == null ? "" : s).replace(/[&<>"]/g,
  c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Open a branded, printable version of the plan — student can save as PDF
// and print it to stick on their wall.
function printPlan(plan, studentName) {
  const win = window.open("", "_blank");
  if (!win) { alert("Please allow pop-ups to print the schedule."); return; }
  const head = `<tr><th class="item">Item</th>` +
    PLAN_MONTHS.map(m => `<th>${m.m}<br><span class="yr">${m.y}</span></th>`).join("") + `</tr>`;
  const rows = plan.map(r => {
    const tds = PLAN_MONTHS.map((m, idx) => {
      const has = r.cells && (idx in r.cells);
      if (!has) return "<td></td>";
      const c = planCell(r.cells[idx]);
      return `<td style="background:${PLAN_COLORS[c.c % PLAN_COLORS.length]};color:#fff;` +
        `font-weight:700">${esc(c.t)}</td>`;
    }).join("");
    return `<tr><td class="item">${esc(r.name)}</td>${tds}</tr>`;
  }).join("");
  win.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>NM Squad — Plan</title><style>` +
    `@page{size:landscape;margin:10mm;}` +
    `*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}` +
    `body{font-family:Arial,Helvetica,sans-serif;color:#1A1A1E;margin:0;padding:20px;}` +
    `.hdr{display:flex;align-items:center;gap:16px;border-bottom:3px solid #F2B100;` +
    `padding-bottom:13px;margin-bottom:18px;}` +
    `.hdr img{width:70px;height:70px;object-fit:contain;}` +
    `.brand{font-size:26px;font-weight:900;letter-spacing:-.02em;}` +
    `.sub{font-size:13px;color:#5C5C66;margin-top:2px;}` +
    `table{border-collapse:collapse;width:100%;font-size:10px;}` +
    `th,td{border:1px solid #CFCFD4;padding:5px 5px;text-align:center;}` +
    `th{background:#F4F4F5;font-weight:800;}` +
    `th .yr{font-weight:400;color:#999;font-size:8px;}` +
    `td.item,th.item{text-align:left;font-weight:800;min-width:130px;}` +
    `.foot{margin-top:16px;font-size:10px;color:#9A9AA2;}` +
    `</style></head><body onload="window.focus();window.print();">` +
    `<div class="hdr"><img src="${window.location.origin}${mascotImg}" alt="NM Squad"/>` +
    `<div><div class="brand">NM Squad</div>` +
    `<div class="sub">${esc(studentName) || "Student"} &mdash; College Application Plan</div></div></div>` +
    `<table><thead>${head}</thead><tbody>${rows ||
      `<tr><td colspan="${PLAN_MONTHS.length + 1}">No plan items.</td></tr>`}</tbody></table>` +
    `<div class="foot">NM Squad &middot; The University Abroad Program</div>` +
    `</body></html>`);
  win.document.close();
}
function PlanTab({ plan, team, save, studentName }) {
  function setRow(i, patch) { save(plan.map((r, j) => j === i ? { ...r, ...patch } : r)); }
  function toggle(i, idx) {
    const cells = { ...(plan[i].cells || {}) };
    if (idx in cells) delete cells[idx]; else cells[idx] = { t: "", c: 0 };
    setRow(i, { cells });
  }
  function setCell(i, idx, patch) {
    const cells = { ...(plan[i].cells || {}) };
    cells[idx] = { ...planCell(cells[idx]), ...patch };
    setRow(i, { cells });
  }
  const sticky = { position: "sticky", left: 0, zIndex: 1, background: CARD };
  return (
    <div className="sp-rise">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: MUTE, fontWeight: 600 }}>
          Month-by-month plan. {team ? "Click a cell to mark it active, then type the milestone." : ""}
        </div>
        <button className="sp-btn gold" onClick={() => printPlan(plan, studentName)}>
          Print schedule
        </button>
      </div>
      <div style={{ overflowX: "auto", border: `1.5px solid ${LINE}`, borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ ...sticky, minWidth: 168, textAlign: "left", padding: "8px 10px",
                borderBottom: `1.5px solid ${LINE}`, borderRight: `1.5px solid ${LINE}` }}
                className="sp-mono">
                <span style={{ fontSize: 10, color: MUTE }}>Item</span>
              </th>
              {PLAN_MONTHS.map((mo, idx) => (
                <th key={idx} style={{ minWidth: 80, padding: "5px 4px", textAlign: "center",
                  borderBottom: `1.5px solid ${LINE}`, borderRight: `1px solid ${LINE}` }}>
                  <div style={{ fontWeight: 800, color: INK, fontSize: 12 }}>{mo.m}</div>
                  <div style={{ fontSize: 9, color: MUTE }}>{mo.y}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plan.map((r, i) => (
              <tr key={r.id || i}>
                <td style={{ ...sticky, minWidth: 168, borderBottom: `1px solid ${LINE}`,
                  borderRight: `1.5px solid ${LINE}` }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input className="sp-in" disabled={!team} value={r.name || ""}
                      placeholder="Plan item" onChange={e => setRow(i, { name: e.target.value })}
                      style={{ border: "none", borderRadius: 0, fontWeight: 700, fontSize: 12.5,
                        background: "transparent" }} />
                    {team && (
                      <button onClick={() => save(plan.filter((_, j) => j !== i))}
                        style={{ border: "none", background: "none", color: MUTE, cursor: "pointer",
                          fontSize: 15, padding: "0 8px" }}>×</button>
                    )}
                  </div>
                </td>
                {PLAN_MONTHS.map((mo, idx) => {
                  const on = r.cells && (idx in r.cells);
                  const cell = on ? planCell(r.cells[idx]) : null;
                  return (
                    <td key={idx} onClick={() => team && !on && toggle(i, idx)}
                      style={{ minWidth: 80, height: 40, padding: 0, position: "relative",
                        borderBottom: `1px solid ${LINE}`, borderRight: `1px solid ${LINE}`,
                        background: cell ? PLAN_COLORS[cell.c % PLAN_COLORS.length] : "#fff",
                        cursor: team && !on ? "pointer" : "default" }}>
                      {cell && (team ? (
                        <input value={cell.t}
                          onChange={e => setCell(i, idx, { t: e.target.value })}
                          style={{ width: "100%", height: "100%", border: "none", outline: "none",
                            background: "transparent", color: "#fff", fontWeight: 700, fontSize: 11,
                            textAlign: "center", fontFamily: "'Nunito',sans-serif", padding: "0 14px" }} />
                      ) : (
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 11,
                          textAlign: "center", padding: "0 3px" }}>{cell.t}</div>
                      ))}
                      {cell && team && (
                        <>
                          <button title="Change colour"
                            onClick={e => { e.stopPropagation();
                              setCell(i, idx, { c: (cell.c + 1) % PLAN_COLORS.length }); }}
                            style={{ position: "absolute", top: 3, left: 3, width: 11, height: 11,
                              borderRadius: 999, border: "1.5px solid rgba(255,255,255,.9)",
                              background: "transparent", cursor: "pointer", padding: 0 }} />
                          <button onClick={e => { e.stopPropagation(); toggle(i, idx); }}
                            style={{ position: "absolute", top: 1, right: 2, border: "none",
                              background: "none", color: "rgba(255,255,255,.9)", cursor: "pointer",
                              fontSize: 11, lineHeight: 1, padding: 0 }}>×</button>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {plan.length === 0 && (
              <tr><td colSpan={PLAN_MONTHS.length + 1} style={{ padding: "26px",
                textAlign: "center", fontSize: 13, fontWeight: 700, color: MUTE }}>
                No plan items yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {team && (
        <button className="sp-btn" style={{ marginTop: 14 }}
          onClick={() => save([...plan, { id: Date.now().toString(36), name: "", cells: {} }])}>
          + Add plan item</button>
      )}
    </div>
  );
}

/* ════════════════ CHECKLIST ════════════════ */
function ChecklistTab({ items, team, save }) {
  const [newLabel, setNewLabel] = useState("");
  const list = Array.isArray(items) ? items : [];
  const done = list.filter(x => x.done).length;
  const pct = list.length ? Math.round((done / list.length) * 100) : 0;

  function toggle(i) {
    save(list.map((x, idx) => idx === i ? { ...x, done: !x.done } : x));
  }
  function update(i, label) {
    save(list.map((x, idx) => idx === i ? { ...x, label } : x));
  }
  function add() {
    const v = newLabel.trim();
    if (!v) return;
    save([...list, { label: v, done: false }]);
    setNewLabel("");
  }
  function remove(i) { save(list.filter((_, idx) => idx !== i)); }
  function markAll(done) { save(list.map(x => ({ ...x, done }))); }

  return (
    <div className="sp-rise" style={{ display: "grid", gap: 14 }}>
      {/* progress + bulk */}
      <div className="sp-card" style={{ padding: "16px 20px", display: "flex",
        alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO,
            letterSpacing: ".06em" }}>PROGRESS</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
            <div style={{ flex: 1, height: 8, background: "#EEE", borderRadius: 999 }}>
              <div style={{ width: `${pct}%`, height: "100%",
                background: pct === 100 ? "#16A34A" : GOLD, borderRadius: 999 }} />
            </div>
            <span className="sp-mono" style={{ fontSize: 12, fontWeight: 800,
              color: pct === 100 ? "#16A34A" : GOLD_LO, minWidth: 70, textAlign: "right" }}>
              {done}/{list.length} · {pct}%
            </span>
          </div>
        </div>
        {team && list.length > 0 && (
          <div style={{ display: "flex", gap: 6 }}>
            <button className="sp-btn ghost" onClick={() => markAll(true)}>Mark all done</button>
            <button className="sp-btn ghost" onClick={() => markAll(false)}>Reset</button>
          </div>
        )}
      </div>

      {/* items */}
      {list.length === 0 ? (
        <div className="sp-card" style={{ padding: "28px 20px", textAlign: "center",
          fontSize: 13, color: MUTE, fontWeight: 600 }}>
          No checklist items yet.{team && " Add the first one below."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map((it, i) => (
            <div key={i} className="sp-card" style={{ padding: "10px 14px",
              display: "flex", alignItems: "center", gap: 11,
              background: it.done ? "#F3FBF5" : CARD,
              border: `1.5px solid ${it.done ? "#86EFAC" : LINE}` }}>
              <button onClick={() => team && toggle(i)} disabled={!team}
                style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                  border: `2px solid ${it.done ? "#16A34A" : LINE}`,
                  background: it.done ? "#16A34A" : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: team ? "pointer" : "default" }}>
                {it.done && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="4"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              {team ? (
                <input value={it.label}
                  onChange={e => update(i, e.target.value)}
                  style={{ flex: 1, border: "none", outline: "none", background: "none",
                    fontSize: 14, fontWeight: 700,
                    color: it.done ? MUTE : INK,
                    textDecoration: it.done ? "line-through" : "none" }} />
              ) : (
                <span style={{ flex: 1, fontSize: 14, fontWeight: 700,
                  color: it.done ? MUTE : INK,
                  textDecoration: it.done ? "line-through" : "none" }}>{it.label}</span>
              )}
              {team && (
                <button onClick={() => remove(i)} title="Remove"
                  style={{ background: "none", border: "none", color: "#B91C1C",
                    fontSize: 12, fontWeight: 800, cursor: "pointer", padding: "4px 8px" }}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {team && (
        <div className="sp-card" style={{ padding: "12px 14px", display: "flex", gap: 8 }}>
          <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="Add a new checklist item…"
            style={{ flex: 1, padding: "9px 12px", fontSize: 13.5, fontWeight: 600,
              border: `1.5px solid ${LINE}`, borderRadius: 8, outline: "none" }} />
          <button className="sp-btn" onClick={add} disabled={!newLabel.trim()}
            style={{ opacity: newLabel.trim() ? 1 : 0.5 }}>Add</button>
        </div>
      )}
    </div>
  );
}

/* ════════════════ NOTES ════════════════ */
function NotesTab({ notes, team, save }) {
  const [text, setText] = useState(notes || "");
  const [savedText, setSavedText] = useState(notes || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(notes || ""); setSavedText(notes || ""); }, [notes]);

  async function flush() {
    if (text === savedText) return;
    setBusy(true);
    try { await save({ notes: text }); setSavedText(text); }
    finally { setBusy(false); }
  }

  return (
    <div className="sp-rise sp-card" style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO,
          letterSpacing: ".06em" }}>COUNSELLOR NOTES</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: MUTE }}>
          {team ? (text === savedText ? (busy ? "Saving…" : "Saved") : "Unsaved · click outside to save") : "Read-only"}
        </span>
      </div>
      {team ? (
        <textarea value={text} onChange={e => setText(e.target.value)}
          onBlur={flush} placeholder="Notes only the NM Squad team sees. Autosaved when you click out."
          style={{ width: "100%", minHeight: 320, padding: "12px 14px", fontSize: 14,
            lineHeight: 1.55, color: INK, fontFamily: "'Archivo',sans-serif",
            border: `1.5px solid ${LINE}`, borderRadius: 10, outline: "none",
            resize: "vertical", boxSizing: "border-box" }} />
      ) : text ? (
        <div style={{ fontSize: 14, lineHeight: 1.6, color: INK, whiteSpace: "pre-wrap" }}>
          {text}
        </div>
      ) : (
        <div style={{ fontSize: 13.5, color: MUTE, fontWeight: 600, padding: "30px 0",
          textAlign: "center" }}>No notes yet.</div>
      )}
    </div>
  );
}

/* ════════════════ TO DO ════════════════ */
function TodoTab({ todos, team, save }) {
  const t = todos && typeof todos === "object" ? todos
    : { student: [], parents: [], nm: [], nmSquad: [] };
  function update(col, list) { save({ todos: { ...t, [col]: list } }); }

  const COLS = [
    { key: "student", label: "Student", color: GOLD },
    { key: "parents", label: "Parents", color: "#3B82F6" },
    { key: "nm",      label: "NM",      color: "#22C55E" },
    { key: "nmSquad", label: "NM Squad", color: "#EC4899" },
  ];

  return (
    <div className="sp-rise" style={{ display: "grid", gap: 14,
      gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
      {COLS.map(c => (
        <TodoColumn key={c.key} label={c.label} color={c.color}
          items={Array.isArray(t[c.key]) ? t[c.key] : []}
          team={team}
          onChange={list => update(c.key, list)} />
      ))}
    </div>
  );
}
function TodoColumn({ label, color, items, team, onChange }) {
  const [draft, setDraft] = useState("");
  const open = items.filter(x => !x.done).length;
  function add() {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, typeof v === "string" ? { label: v, done: false } : v]);
    setDraft("");
  }
  // Items may be plain strings (legacy) or {label, done} (new). Normalise.
  const list = items.map(x => typeof x === "string" ? { label: x, done: false } : x);
  function toggle(i) {
    onChange(list.map((x, idx) => idx === i ? { ...x, done: !x.done } : x));
  }
  function remove(i) { onChange(list.filter((_, idx) => idx !== i)); }

  return (
    <div className="sp-card" style={{ padding: 14, background: "#FAFAFA",
      border: `1.5px solid ${LINE}`, minHeight: 200 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
        marginBottom: 10, paddingBottom: 8, borderBottom: `1.5px solid ${LINE}` }}>
        <span style={{ width: 9, height: 9, borderRadius: 999, background: color }} />
        <span style={{ fontSize: 13, fontWeight: 900, color: INK,
          textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800,
          color: MUTE, fontFamily: "'Space Mono',monospace" }}>
          {open}/{list.length}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {list.map((it, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 7,
            background: "#fff", borderRadius: 8, padding: "8px 10px",
            border: `1px solid ${LINE}`, opacity: it.done ? 0.55 : 1 }}>
            <button onClick={() => team && toggle(i)} disabled={!team}
              style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${it.done ? color : LINE}`,
                background: it.done ? color : "#fff",
                cursor: team ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              {it.done && (
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="5"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700,
              color: it.done ? MUTE : INK,
              textDecoration: it.done ? "line-through" : "none",
              wordBreak: "break-word" }}>{it.label}</span>
            {team && (
              <button onClick={() => remove(i)} title="Remove"
                style={{ background: "none", border: "none", color: MUTE,
                  fontSize: 14, cursor: "pointer", padding: "0 4px" }}>×</button>
            )}
          </div>
        ))}
        {list.length === 0 && (
          <div style={{ fontSize: 11.5, color: MUTE, fontWeight: 600, padding: 8,
            textAlign: "center" }}>(no items)</div>
        )}
      </div>
      {team && (
        <div style={{ marginTop: 10, display: "flex", gap: 5 }}>
          <input value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="Add task…"
            style={{ flex: 1, padding: "7px 10px", fontSize: 12, fontWeight: 600,
              border: `1.5px solid ${LINE}`, borderRadius: 6, outline: "none" }} />
          <button onClick={add} disabled={!draft.trim()}
            style={{ background: color, color: "#fff", border: "none",
              borderRadius: 6, padding: "0 12px", fontSize: 12, fontWeight: 800,
              cursor: draft.trim() ? "pointer" : "default", opacity: draft.trim() ? 1 : 0.5 }}>+</button>
        </div>
      )}
    </div>
  );
}

/* ════════════════ SETTINGS ════════════════ */
function Toggle({ on, onClick, label }) {
  return (
    <div onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 11,
      cursor: "pointer", padding: "11px 0" }}>
      <span style={{ width: 42, height: 24, borderRadius: 999, flexShrink: 0,
        background: on ? GOLD : "#D8D3C0", position: "relative", transition: ".15s" }}>
        <span style={{ position: "absolute", top: 3, left: on ? 21 : 3, width: 18, height: 18,
          borderRadius: 999, background: "#fff", transition: ".15s" }} />
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>{label}</span>
    </div>
  );
}
function SettingsTab({ student, save }) {
  const [email, setEmail] = useState("");
  const [level, setLevel] = useState("view");
  const access = student.access || [];
  const nttOn = !!(student.ntt || student.nttTaken);
  function addEmail() {
    const e = email.trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    save({ access: [...access.filter(x => x.email !== e), { email: e, level }] });
    setEmail("");
  }
  return (
    <div className="sp-rise" style={{ display: "grid", gap: 14 }}>
      <div className="sp-card" style={{ padding: "8px 20px" }}>
        <div className="sp-mono" style={{ fontSize: 11, color: GOLD_LO, padding: "14px 0 2px" }}>
          Access</div>
        <Toggle on={!!student.editAccess} label="Student can edit their own portal"
          onClick={() => save({ editAccess: !student.editAccess })} />
        <div style={{ borderTop: `1px solid ${LINE}` }} />
        <Toggle on={nttOn} label="Student has taken the NTT — shows the NTT tab"
          onClick={() => save({ nttTaken: !nttOn })} />
      </div>
      <div className="sp-card" style={{ padding: 20 }}>
        <div className="sp-mono" style={{ fontSize: 11, color: GOLD_LO, marginBottom: 5 }}>
          Share access</div>
        <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 13 }}>
          Give a specific email view or edit access to this student's portal.</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input className="sp-in" placeholder="name@email.com" value={email}
            onChange={e => setEmail(e.target.value)} style={{ flex: "1 1 200px" }} />
          <select className="sp-in" value={level} onChange={e => setLevel(e.target.value)}
            style={{ width: 110 }}>
            <option value="view">View</option>
            <option value="edit">Edit</option>
          </select>
          <button className="sp-btn gold" onClick={addEmail}>Add</button>
        </div>
        {access.map(x => (
          <div key={x.email} style={{ display: "flex", alignItems: "center", gap: 10,
            padding: "10px 0", borderBottom: `1px solid ${LINE}` }}>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: INK }}>{x.email}</span>
            <span className="sp-mono" style={{ fontSize: 10, color: x.level === "edit" ? GOLD_LO : MUTE,
              border: `1px solid ${LINE}`, borderRadius: 4, padding: "3px 7px" }}>{x.level}</span>
            <button className="sp-btn ghost" style={{ padding: "5px 10px" }}
              onClick={() => save({ access: access.filter(a => a.email !== x.email) })}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════ MAIN ════════════════ */
export default function StudentPortal({ student, role = "student", onRequestEdit, onlyTab }) {
  const team = role === "team";
  // The NM Squad team always edits; a student edits once edit access is approved.
  const canEdit = team || !!student.editAccess;
  const [tab, setTab] = useState("Profile");
  const [files, setFiles] = useState({});
  const [lb, setLb] = useState(null);
  const [uploading, setUploading] = useState({});
  // Save status: 'idle' | 'saving' | 'saved' | 'error'
  const [saveStatus, setSaveStatus] = useState("idle");
  const saveTimerRef = useRef(null);
  const pendingPatchRef = useRef({});
  const a = student.onbAnswers || {};

  useEffect(() => { getTuapFiles(student.id).then(setFiles).catch(() => {}); }, [student.id]);

  // Debounced save — keystrokes coalesce into one Firestore write every 800ms.
  // Shallow-merge patches so multiple field updates within the window all land.
  function save(patch) {
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    setSaveStatus("saving");
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const p = pendingPatchRef.current;
      pendingPatchRef.current = {};
      try {
        await updateTuapStudent(student.id, p);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(s => (s === "saved" ? "idle" : s)), 1800);
      } catch (e) {
        console.error("Save failed:", e.message);
        setSaveStatus("error");
      }
    }, 800);
  }
  async function onUpload(key, file) {
    setUploading(u => ({ ...u, [key]: true }));
    try {
      await uploadOnbFile(student, key, file);
      setFiles(await getTuapFiles(student.id));
    } catch (e) { alert(e.message || "Upload failed."); }
    finally { setUploading(u => ({ ...u, [key]: false })); }
  }

  // Only surface the top responses in the portal/admin views.
  // Interests: show only "Very Interested" items. Community: show only "Yes" items.
  const interests = INTEREST_SUBJECTS
    .filter(s => (a.interests || {})[s] === "Very Interested")
    .map(s => ({ key: s, label: s, rating: a.interests[s] }));
  const values = (a.community ? Object.keys(a.community) : [])
    .filter(k => a.community[k] === "Yes")
    .map(k => ({ key: k, label: k, rating: a.community[k] }));

  const nttOn = !!student.ntt || student.nttTaken || (files.nttScore != null);
  const TABS = onlyTab ? [onlyTab] : ["Profile", "Credentials", ...(nttOn ? ["NTT"] : []),
    "Interests", "Values", "Academic", "College List", "Activities",
    "Recommendations", "Plan", "Essays", "Checklist", "To Do",
    ...(team ? ["Notes", "Settings"] : [])];
  const activeTab = onlyTab || (TABS.includes(tab) ? tab : "Profile");

  // Tiny pill that flashes 'Saving…' → 'Saved' (or 'Save failed') in the corner.
  const statusPill = saveStatus === "idle" ? null : (
    <div style={{ position: "fixed", bottom: 70, right: 18, zIndex: 60,
      padding: "7px 14px", borderRadius: 999, fontSize: 12, fontWeight: 800,
      fontFamily: "'Space Mono',monospace", letterSpacing: ".06em",
      textTransform: "uppercase", boxShadow: "0 4px 14px rgba(20,20,30,.15)",
      background: saveStatus === "error" ? "#FEE2E2"
        : saveStatus === "saving" ? "#FEF3C7"
        : "#DCFCE7",
      color: saveStatus === "error" ? "#B91C1C"
        : saveStatus === "saving" ? "#A16207"
        : "#15803D",
      animation: "spIn .2s cubic-bezier(.2,.7,.2,1) both" }}>
      {saveStatus === "error" ? "Save failed"
        : saveStatus === "saving" ? "Saving…"
        : "Saved"}
    </div>
  );

  return (
    <div className="sp" style={{ background: BG, minHeight: team ? "auto" : "100vh" }}>
      <Styles />
      <style>{`@keyframes spIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}`}</style>
      {statusPill}
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: team ? "4px 0 30px" : "22px 26px 56px" }}>
        {!team && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div className="sp-disp" style={{ fontSize: 22, fontWeight: 600, color: INK }}>
                {student.studentName || "My portal"}</div>
              {onlyTab && (
                <div style={{ fontSize: 13, color: MUTE, fontWeight: 600, marginTop: 2 }}>
                  NM Squad · Essay submission</div>
              )}
            </div>
            {!onlyTab && !student.editAccess && onRequestEdit && (
              <button className="sp-btn ghost" onClick={onRequestEdit}>
                {student.editRequestedAt ? "Edit request sent" : "Request edit access"}</button>
            )}
          </div>
        )}

        {!onlyTab && (
          <div style={{ display: "flex", gap: 20, overflowX: "auto", marginBottom: 22,
            borderBottom: `1.5px solid ${LINE}` }}>
            {TABS.map(t => (
              <button key={t} className={"sp-tab" + (activeTab === t ? " on" : "")}
                onClick={() => setTab(t)}>{t}</button>
            ))}
          </div>
        )}

        {activeTab === "Profile" && (
          <ProfileTab a={a} photoUrl={student.photoUrl} files={files} onView={setLb}
            team={canEdit} save={save} />
        )}
        {activeTab === "Credentials" && (
          <CredentialsTab creds={student.credentials || []} team={canEdit}
            save={creds => save({ credentials: creds })} />
        )}
        {activeTab === "NTT" && (
          <NttTab student={student} files={files} team={team} save={save} onUpload={onUpload} />
        )}
        {activeTab === "Interests" && (
          <RatingList blurb={`The interests this student rated "Very Interested".`}
            items={interests} order={["Very Interested"]}
            team={team} meta={student.interestMeta || {}}
            save={m => save({ interestMeta: m })} />
        )}
        {activeTab === "Values" && (
          <RatingList blurb={`The causes this student said yes to.`}
            items={values} order={["Yes"]}
            team={team} meta={student.valuesMeta || {}}
            save={m => save({ valuesMeta: m })} />
        )}
        {activeTab === "Academic" && (
          <AcademicTab a={a} student={student} files={files} team={canEdit}
            save={save} onUpload={onUpload} onView={setLb} />
        )}
        {activeTab === "College List" && (
          <CollegeListTab colleges={student.colleges || []} team={canEdit}
            save={cols => save({ colleges: cols })} />
        )}
        {activeTab === "Activities" && (
          <ActivitiesTab acts={student.activities || []} team={canEdit}
            save={acts => save({ activities: acts })} />
        )}
        {activeTab === "Recommendations" && (
          <RecommendationsTab recos={student.recos || []} team={canEdit}
            save={recos => save({ recos })} files={files} uploading={uploading}
            onUpload={onUpload} onView={setLb} />
        )}
        {activeTab === "Plan" && (
          <PlanTab plan={student.plan || []} team={canEdit}
            save={plan => save({ plan })} studentName={student.studentName} />
        )}
        {activeTab === "Essays" && <EssaysTab student={student} team={team} />}
        {activeTab === "Checklist" && (
          <ChecklistTab items={student.checklist || []} team={canEdit}
            save={items => save({ checklist: items })} />
        )}
        {activeTab === "To Do" && (
          <TodoTab todos={student.todos} team={canEdit}
            save={patch => save(patch)} />
        )}
        {activeTab === "Notes" && team && (
          <NotesTab notes={student.notes || ""} team={canEdit} save={save} />
        )}
        {activeTab === "Settings" && team && (
          <SettingsTab student={student} save={save} />
        )}
      </div>
      <Lightbox file={lb} onClose={() => setLb(null)} />
    </div>
  );
}
